import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import EntityCard from '@payaca/components/entityCard/EntityCard';
import ServicePlanSubscribersList from './ServicePlanSubscribersList';

import type {
  ListedServicePlan,
  PublicHydratedServicePlanDiscountCode,
} from '@payaca/types/service-plans';

import { useRegion } from '@/api/rest/users/useRegion';

import { currencyPrice } from '@payaca/helpers/financeHelper';
import { getAccountRegionFromCurrencyCodeLegacy } from '@payaca/helpers/internationalHelper';
import {
  getServicePlanMonthlyPrice,
  getServicePlanYearlyPrice,
} from '@payaca/helpers/servicePlanHelper';

import { usePermissions } from '@/hooks/usePermissions';
import { Button, Modal, useToastContext } from '@payaca/ambrosia';
import LabelValuePair from '@payaca/components/labelValuePair/LabelValuePair';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import Tooltip from '@payaca/components/tooltip/Tooltip';
import { ServicePlanPermissions } from '@payaca/permissions/service-plans/service-plans.permissions';
import * as servicePlansActions from '@payaca/store/servicePlans/servicePlansActions';
import UntitledIcon from '@payaca/untitled-icons';
import CopyServicePlanPublicLinkModal, {
  useCopyServicePlanPublicLinkToClipboard,
} from '../copyServicePlanPublicLinkModal/CopyServicePlanPublicLinkModal';
import './ListedServicePlan.sass';

type Props = {
  listedServicePlan: ListedServicePlan;
  openInviteCustomerModal: () => void;
};
const ListedServicePlan = ({
  listedServicePlan,
  openInviteCustomerModal,
}: Props) => {
  const dispatch = useDispatch();
  const region = useRegion();
  const history = useHistory();
  const { pushError } = useToastContext();
  const [showSubscribers, setShowSubscribers] = useState(false);
  const [showDeleteServicePlanModal, setShowDeleteServicePlanModal] =
    useState(false);
  const [
    showCopyServicePlanPublicLinkModal,
    setShowCopyServicePlanPublicLinkModal,
  ] = useState(false);
  const [servicePlanDiscountCodes, setSericePlanDiscountCodes] = useState<
    PublicHydratedServicePlanDiscountCode[]
  >([]);
  const [isDeletingServicePlan, setIsDeletingServicePlan] = useState(false);

  useEffect(() => {
    dispatch(
      servicePlansActions.getServicePlanDiscountCodes.request({
        publicId: listedServicePlan.publicId,
        callback: (res) => setSericePlanDiscountCodes(res),
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [listedServicePlan]);

  const monthlyPrice = useMemo(() => {
    return getServicePlanMonthlyPrice(listedServicePlan.prices);
  }, [listedServicePlan.prices]);

  const yearlyPrice = useMemo(() => {
    return getServicePlanYearlyPrice(listedServicePlan.prices);
  }, [listedServicePlan.prices]);

  const planCanBeSold = useMemo(
    () => listedServicePlan.planCanBeSold,
    [listedServicePlan]
  );

  const { userHasRequiredPermission } = usePermissions();

  const copyServicePlanPublicLinkToClipboard =
    useCopyServicePlanPublicLinkToClipboard();

  const quickActions = useMemo(() => {
    const qa = [
      {
        actionName: 'View service periods',
        actionBehaviour: () =>
          history.push(
            `/service-plans/service-periods?servicePlanPublicIds=${listedServicePlan.publicId}`
          ),
      },
    ];
    if (planCanBeSold) {
      qa.push({
        actionName: 'Copy public link',
        actionBehaviour: () => {
          if (servicePlanDiscountCodes.length > 0) {
            setShowCopyServicePlanPublicLinkModal(true);
          } else {
            void copyServicePlanPublicLinkToClipboard(
              listedServicePlan.publicId
            );
          }
        },
      });
    }
    if (
      userHasRequiredPermission([ServicePlanPermissions.PERSIST_SERVICE_PLAN])
    ) {
      qa.push({
        actionName: 'Edit',
        actionBehaviour: () =>
          history.push(
            `/service-plans/service-plan/${listedServicePlan.publicId}`
          ),
      });

      const hasAnySubscribers = listedServicePlan.subscriptions.some(
        (x) => x.servicePlanPublicId === listedServicePlan.publicId
      );
      if (!hasAnySubscribers) {
        qa.push({
          actionName: 'Delete',
          actionBehaviour: () => setShowDeleteServicePlanModal(true),
        });
      }
    }
    return qa;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'listedServicePlan.subscriptions', and 'history'
  }, [
    listedServicePlan.publicId,
    planCanBeSold,
    userHasRequiredPermission,
    servicePlanDiscountCodes,
    copyServicePlanPublicLinkToClipboard,
  ]);

  const getRoundedMrrPrice = (mrr: number) => {
    if (mrr < 10000) {
      return currencyPrice(Math.round(mrr / 100) * 100, region, false); // less than 100, round to nearest £1
    } else if (mrr < 100000) {
      return currencyPrice(Math.round(mrr / 1000) * 1000, region, false); // less than 1000, round to neared £10
    } else {
      return `${currencyPrice(Math.round(mrr / 10000) * 10, region, false)}k`; // round to nearest £100
    }
  };

  const monthlyTaxLabel = useMemo(() => {
    return monthlyPrice?.currencyCode
      ? ` (${monthlyPrice?.priceIncludesTax ? 'inc' : 'ex'} tax)`
      : '';
  }, [monthlyPrice?.currencyCode, monthlyPrice?.priceIncludesTax]);
  const yearlyTaxLabel = useMemo(() => {
    return yearlyPrice?.currencyCode
      ? ` (${yearlyPrice?.priceIncludesTax ? 'inc' : 'ex'} tax)`
      : '';
  }, [yearlyPrice?.currencyCode, yearlyPrice?.priceIncludesTax]);

  const numPendingSubscriptions = listedServicePlan.subscriptions.filter(
    (x) => x.status === 'pending'
  ).length;

  const handleDeleteServicePlan = () => {
    setIsDeletingServicePlan(true);

    dispatch(
      servicePlansActions.deleteServicePlan.request({
        servicePlanPublicId: listedServicePlan.publicId,
        callback: () => {
          dispatch(servicePlansActions.getListedServicePlans.request({}));
          setIsDeletingServicePlan(false);
          setShowDeleteServicePlanModal(false);
        },
        onErrorCallback: () => {
          setIsDeletingServicePlan(false);
          pushError(
            'Something went wrong deleting your service plan. Please try again later or contact support.'
          );
        },
      })
    );
  };

  return (
    <>
      <EntityCard
        className="listed-service-plan"
        onClick={() =>
          !!listedServicePlan.subscriptions.length &&
          setShowSubscribers(!showSubscribers)
        }
        quickActionsConfig={{
          recordId: listedServicePlan.publicId,
          renderDisabledIfNoActions: true,
          quickActions,
        }}
      >
        <ResponsiveViewWrapper
          className="listed-service-plan-card-content"
          downBreakpointSm={600}
          downBreakpointXs={400}
        >
          <h3>{listedServicePlan.name}</h3>
          <div className="data-wrapper">
            <LabelValuePair
              label="Subscribers"
              value={`${listedServicePlan.stats.numActiveSubscriptions}${
                numPendingSubscriptions
                  ? ` (${numPendingSubscriptions} pending)`
                  : ''
              }`}
              suffixLabelWith=""
            />
          </div>
          <div className="data-wrapper">
            <Tooltip
              text={`Monthly recurring revenue${
                monthlyTaxLabel || yearlyTaxLabel
              }`}
            >
              <LabelValuePair
                label="MRR"
                value={
                  listedServicePlan.stats.monthlyRecurringRevenue
                    ? getRoundedMrrPrice(
                        listedServicePlan.stats.monthlyRecurringRevenue
                      )
                    : '-'
                }
                suffixLabelWith=""
              />
            </Tooltip>
          </div>
          {/* prices */}
          <div className="data-wrapper">
            <Tooltip text={`Monthly price${monthlyTaxLabel}`}>
              <LabelValuePair
                label="Price (month)"
                value={
                  monthlyPrice?.basicPrice || monthlyPrice?.basicPrice === 0
                    ? currencyPrice(
                        monthlyPrice.basicPrice,
                        monthlyPrice.currencyCode
                          ? getAccountRegionFromCurrencyCodeLegacy(
                              monthlyPrice.currencyCode
                            )
                          : undefined
                      )
                    : '-'
                }
                suffixLabelWith=""
              />
            </Tooltip>
          </div>
          <div className="data-wrapper">
            <Tooltip text={`Annual price${yearlyTaxLabel}`}>
              <LabelValuePair
                label="Price (year)"
                value={
                  yearlyPrice?.basicPrice
                    ? currencyPrice(
                        yearlyPrice.basicPrice,
                        yearlyPrice.currencyCode
                          ? getAccountRegionFromCurrencyCodeLegacy(
                              yearlyPrice.currencyCode
                            )
                          : undefined
                      )
                    : '-'
                }
                suffixLabelWith=""
              />
            </Tooltip>
          </div>
          {/* create send invite to customer */}
          <div className="invite-button-wrapper">
            {planCanBeSold && (
              <div onClick={(e) => e.stopPropagation()}>
                <Button
                  onClick={openInviteCustomerModal}
                  variant="link"
                  className="px-0 py-2"
                >
                  Invite customer
                </Button>
              </div>
            )}
            {!planCanBeSold && listedServicePlan.planCanBeSoldResultReason && (
              <Tooltip text={listedServicePlan.planCanBeSoldResultReason}>
                <UntitledIcon name="info-circle.3" />
              </Tooltip>
            )}
          </div>
        </ResponsiveViewWrapper>
        {showSubscribers && (
          <ServicePlanSubscribersList
            subscribers={listedServicePlan.subscriptions}
            servicePlanPrices={listedServicePlan.prices}
          />
        )}
      </EntityCard>
      <Modal
        isOpen={showDeleteServicePlanModal}
        onClose={() => setShowDeleteServicePlanModal(false)}
        title="Notice"
      >
        <Modal.Body>
          <p>Are you sure you want to delete this service plan?</p>
          <p>
            <strong>
              This action will not be reflected in any third-party integrations.
            </strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              colour="negative"
              isProcessing={isDeletingServicePlan}
              replaceContentWhenProcessing
              disabled={isDeletingServicePlan}
              onClick={handleDeleteServicePlan}
            >
              Delete
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
      <CopyServicePlanPublicLinkModal
        servicePlanPublicId={listedServicePlan.publicId}
        servicePlanDiscountCodes={servicePlanDiscountCodes}
        isOpen={showCopyServicePlanPublicLinkModal}
        onClose={() => setShowCopyServicePlanPublicLinkModal(false)}
      />
    </>
  );
};

export default ListedServicePlan;
